/**
 * ShoppingListDrawer — displays the aisle-grouped shopping list from the
 * current meal plan and offers three export paths:
 *   1. Copy to clipboard (plain text, aisle-headed sections)
 *   2. Download .txt file
 *   3. Native share sheet (Capacitor Share plugin on iOS; falls back to
 *      Web Share API on modern browsers; disabled otherwise).
 */
import React from "react";
import ReactDOM from "react-dom";
import { X, Copy, Download, Share2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { isNativeApp } from "../../lib/platform";

const buildPlainText = (shoppingList) => {
  const lines = ["DAIONE — Weekly Shopping List", ""];
  for (const group of shoppingList || []) {
    lines.push(`## ${group.aisle || "Other"}`);
    for (const item of group.items || []) lines.push(`- ${item}`);
    lines.push("");
  }
  return lines.join("\n").trim() + "\n";
};

async function nativeShare(text) {
  if (isNativeApp()) {
    const { Share } = await import("@capacitor/share");
    await Share.share({
      title: "DAIONE — Shopping List",
      text,
      dialogTitle: "Share shopping list",
    });
    return true;
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    await navigator.share({ title: "DAIONE — Shopping List", text });
    return true;
  }
  return false;
}

export default function ShoppingListDrawer({ open, shoppingList, onClose }) {
  if (!open) return null;

  const flatText = buildPlainText(shoppingList);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(flatText);
      toast.success("Shopping list copied");
    } catch (err) {
      toast.error("Could not copy");
    }
  };

  const download = () => {
    const blob = new Blob([flatText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "daione-shopping-list.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const share = async () => {
    try {
      const ok = await nativeShare(flatText);
      if (!ok) {
        await copy();
        toast.info("No share sheet available — copied instead");
      }
    } catch (err) {
      // User dismissed the share sheet; not an error.
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="shopping-list-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-xl bg-[#121212] border border-white/10 sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto"
        data-testid="shopping-list-drawer"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-eyebrow text-[#6B9BFF] mb-1 inline-flex items-center gap-1.5">
              <ShoppingBag className="w-3 h-3" /> Shopping list
            </div>
            <h3 className="font-display text-2xl leading-none">This week&apos;s groceries</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white"
            data-testid="shopping-list-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {(!shoppingList || shoppingList.length === 0) ? (
          <div className="text-sm text-white/50 py-8 text-center italic">
            No shopping list yet — generate a meal plan first.
          </div>
        ) : (
          <div className="space-y-4 mb-5" data-testid="shopping-list-groups">
            {shoppingList.map((group, gi) => (
              <div key={`${group.aisle}-${gi}`} className="border border-white/10 bg-white/[0.03]">
                <div className="px-4 py-2 border-b border-white/10 label-eyebrow text-[#6B9BFF] text-[10px]">
                  {group.aisle || "Other"}
                </div>
                <ul className="p-4 space-y-1.5">
                  {(group.items || []).map((item, ii) => (
                    <li key={`${gi}-${ii}`} className="text-sm text-white/80 flex items-start gap-2">
                      <span className="text-white/30 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {shoppingList && shoppingList.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={copy}
              className="bg-white/5 border border-white/20 hover:border-white/40 text-white font-bold uppercase tracking-widest text-[10px] px-3 py-3 inline-flex items-center justify-center gap-1.5"
              data-testid="shopping-list-copy"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
            <button
              onClick={download}
              className="bg-white/5 border border-white/20 hover:border-white/40 text-white font-bold uppercase tracking-widest text-[10px] px-3 py-3 inline-flex items-center justify-center gap-1.5"
              data-testid="shopping-list-download"
            >
              <Download className="w-3.5 h-3.5" /> .txt
            </button>
            <button
              onClick={share}
              className="bg-[#6B9BFF] hover:bg-white text-black font-black uppercase tracking-widest text-[10px] px-3 py-3 inline-flex items-center justify-center gap-1.5"
              data-testid="shopping-list-share"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
